/* eslint-disable @typescript-eslint/ban-ts-comment */
import { FC } from 'react';
import { useParams } from 'next/navigation';
//@ts-ignore
import { markdown as MD } from 'markdown';
import { Product } from 'shared/types/product/Product';
import Markdown from 'components/commercetools-ui/organisms/markdown';
import { CurrencyHelpers } from 'helpers/currencyHelpers';

export interface ProductPriceProps {
  product: Product;
  beforeText?: string;
  afterText?: string;
  alignment?: 'left' | 'center' | 'right';
  fontWeight?: 'normal' | 'bold';
  italic?: boolean;
}

const alignmentClasses: Record<string, string> = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
};

const fontWeightClasses: Record<string, string> = {
  normal: 'font-normal',
  bold: 'font-bold',
};

// Convert a markdown string to inline HTML (drop the wrapping <p>) so it can be
// merged with the price into a single body-text output.
const toInlineHtml = (value?: string) =>
  value
    ? MD.toHTML(value)
        .replace(/^\s*<p>/, '')
        .replace(/<\/p>\s*$/, '')
    : '';

const ProductPrice: FC<ProductPriceProps> = ({
  product,
  beforeText,
  afterText,
  alignment = 'center',
  fontWeight = 'normal',
  italic = false,
}) => {
  const { locale } = useParams();
  const variant = product?.variants?.[0];

  if (!variant?.price) return null;

  const discountPercentage =
    variant.discountedPrice &&
    (((variant.price?.centAmount as number) - (variant.discountedPrice.centAmount as number)) /
      (variant.price?.centAmount as number)) *
      100;

  const priceHtml = discountPercentage
    ? `<span class="text-gray-500 line-through">${CurrencyHelpers.formatForCurrency(
        variant.price ?? 0,
        locale,
      )}</span> <span class="font-bold text-accent-red">${CurrencyHelpers.formatForCurrency(
        variant.discountedPrice as number,
        locale,
      )}</span>`
    : CurrencyHelpers.formatForCurrency(variant.price as number, locale);

  const combined = [toInlineHtml(beforeText), priceHtml, toInlineHtml(afterText)].filter(Boolean).join(' ');

  const wrapperClassName = [
    alignmentClasses[alignment] ?? alignmentClasses.center,
    fontWeightClasses[fontWeight] ?? fontWeightClasses.normal,
    italic ? 'italic' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div data-id="product-price" className={wrapperClassName}>
      <Markdown markdown={combined} />
    </div>
  );
};

export default ProductPrice;
